var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { KeyboardManager } from '@inter-ikea-kompis/keyboard-manager';
import { HelperText as SkapaHelperText } from '@ingka/helper-text-webc/passive.js';
import { InputField as SkapaInputField } from '@ingka/input-field-webc/passive.js';
import OpenDesignCodeLengthEnum from '../../enums/OpenDesignCodeLengthEnum.js';
import OpenDesignCodeStateEnum from '../../enums/OpenDesignCodeStateEnum.js';
import OpenDesignCodeInputStyle from './OpenDesignCodeInputStyle.js';
/**
 * @event open When keypress = Enter emit open event.
 * @event input Emitted when user writes a character in the input field.
 */
export default class OpenDesignCodeInput extends KompisElement {
    constructor() {
        super(...arguments);
        // Localized information. Deprecated - use "translations" instead.
        this.localizedInformation = null;
        // The translations retrieved from translations service.
        this.translations = null;
        // The state. Valid state are in OpenDesignCodeInputState
        this.state = OpenDesignCodeStateEnum.default;
        // VPC code. Changing this will update the value of the input field. The component tracks the value of the input field itself so you should not update this every time the value changes.
        this.vpcCode = null;
        this.keyboardManager = new KeyboardManager();
    }
    async connectedCallback() {
        this.customElements.define('skapa-input-field', SkapaInputField);
        this.customElements.define('skapa-helper-text', SkapaHelperText);
        super.connectedCallback();
    }
    /**
     * Renders the component.
     *
     * @returns Template.
     */
    render() {
        // We want translations to be required, but since both props to provide translations are optional, this check is needed until localizedInformation is removed.
        if (!this.hasTranslations()) {
            // We can't throw an error because on the first render no props are set.
            return null;
        }
        return html `
			<style>
				${OpenDesignCodeInputStyle.getStyle()}
			</style>
			<skapa-input-field auto-id="open-design-code-input-input" state="regular">
				<label slot="label">${this.getTranslations().loadVpcInputFieldLabel}</label>
				<input
					.value="${this.vpcCode ?? ''}"
					inputmode="text"
					@focusin="${this.onInputFocusIn.bind(this)}"
					@focusout="${this.onInputFocusOut.bind(this)}"
					@keydown="${this.onInputKeydown.bind(this)}"
					@input="${this.onInputValue.bind(this)}"
					aria-required="true"
					minlength="5"
					maxlength="6"
				></input>
				<skapa-helper-text slot="helper">
					${this.getTranslations().loadVpcInputFieldHelpMessage}
				</skapa-helper-text>
			</skapa-input-field>
		`;
    }
    /**
     * Internal method to retrieve zip code directly from input field. When using
     * this component internally, prefer this over listening on the `input` event.
     */
    getVpcCode() {
        return this.getInputElement()?.value.toUpperCase() ?? '';
    }
    /**
     * Internal method to check if VPC code is valid.
     */
    isValidVpcCode() {
        const vpcCode = this.getVpcCode();
        return (vpcCode.length <= OpenDesignCodeLengthEnum.max &&
            vpcCode.length >= OpenDesignCodeLengthEnum.min);
    }
    shouldUpdate(changedProperties) {
        if (changedProperties.has('vpcCode')) {
            const input = this.getInputElement();
            if (input) {
                input.value = this.vpcCode ?? '';
            }
        }
        return true;
    }
    getInputElement() {
        return this.shadowRoot?.querySelector('input');
    }
    hasTranslations() {
        return !!this.translations || !!this.localizedInformation?.translations;
    }
    getTranslations() {
        const translations = this.translations
            ? this.translations
            : this.localizedInformation?.translations;
        return translations;
    }
    onInputFocusIn() {
        const inputElement = this.getInputElement();
        if (inputElement) {
            this.keyboardManager.setFocus(inputElement);
        }
    }
    onInputFocusOut() {
        this.keyboardManager.removeFocus();
    }
    onInputKeydown(event) {
        if (event.key === 'Enter' &&
            this.state !== OpenDesignCodeStateEnum.loading &&
            this.isValidVpcCode()) {
            this.dispatchEvent(new CustomEvent('open', {
                bubbles: true,
                detail: {
                    vpcCode: this.getVpcCode()
                }
            }));
        }
    }
    onInputValue(e) {
        e.stopPropagation();
        this.removeInvalidInputCharacters(e);
        this.dispatchEvent(new CustomEvent('input', {
            bubbles: true,
            detail: {
                vpcCode: this.getVpcCode()
            }
        }));
    }
    removeInvalidInputCharacters(e) {
        const inputElement = e.target;
        const invalidCharactersRegex = /[^a-zA-Z0-9]/g;
        if (invalidCharactersRegex.test(inputElement.value)) {
            inputElement.value = inputElement.value.replace(invalidCharactersRegex, '');
        }
    }
}
__decorate([
    property({ type: Object, required: true })
], OpenDesignCodeInput.prototype, "theme", void 0);
__decorate([
    property({ type: Object })
], OpenDesignCodeInput.prototype, "localizedInformation", void 0);
__decorate([
    property({ type: Object })
], OpenDesignCodeInput.prototype, "translations", void 0);
__decorate([
    property({ type: String })
], OpenDesignCodeInput.prototype, "state", void 0);
__decorate([
    property({ type: String })
], OpenDesignCodeInput.prototype, "vpcCode", void 0);
//# sourceMappingURL=OpenDesignCodeInput.js.map